<template>
    <Head title="Edit Location" />

    <div class="py-6">
        <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="mb-6">
                <Link
                    :href="route('settings.locations.show', location.id)"
                    class="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
                >
                    <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Location
                </Link>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-gray-100">Edit Location</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">Update location information</p>
            </div>

            <!-- Form Card -->
            <form @submit.prevent="submitForm" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div class="p-6 space-y-6">
                    <!-- Basic Information Section -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Name -->
                            <div class="md:col-span-2">
                                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Name <span class="text-red-500">*</span>
                                </label>
                                <input
                                    v-model="form.name"
                                    type="text"
                                    id="name"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="e.g., Main Office Building"
                                />
                                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                            </div>

                            <!-- Code -->
                            <div>
                                <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Code
                                </label>
                                <input
                                    v-model="form.code"
                                    type="text"
                                    id="code"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="e.g., BLDG-01"
                                />
                                <p v-if="form.errors.code" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.code }}</p>
                            </div>

                            <!-- Type -->
                            <div>
                                <label for="type" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Type <span class="text-red-500">*</span>
                                </label>
                                <select
                                    v-model="form.type"
                                    id="type"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="">Select Type</option>
                                    <option v-for="type in locationTypes" :key="type.value" :value="type.value">
                                        {{ type.label }}
                                    </option>
                                </select>
                                <p v-if="form.errors.type" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.type }}</p>
                            </div>

                            <!-- Parent Location -->
                            <div class="md:col-span-2">
                                <label for="parent_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Parent Location
                                </label>
                                <select
                                    v-model="form.parent_id"
                                    id="parent_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option :value="null">None (Root Location)</option>
                                    <option v-for="loc in parentLocations" :key="loc.id" :value="loc.id">
                                        {{ loc.path_name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Select a parent location to create a hierarchical structure</p>
                                <p v-if="form.errors.parent_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.parent_id }}</p>
                            </div>

                            <!-- Description -->
                            <div class="md:col-span-2">
                                <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Description
                                </label>
                                <textarea
                                    v-model="form.description"
                                    id="description"
                                    rows="3"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="Brief description of this location..."
                                ></textarea>
                                <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.description }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Address Section -->
                    <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Address Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Address Line 1 -->
                            <div class="md:col-span-2">
                                <label for="address_line_1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Address Line 1
                                </label>
                                <input
                                    v-model="form.address_line_1"
                                    type="text"
                                    id="address_line_1"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.address_line_1" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.address_line_1 }}</p>
                            </div>

                            <!-- Address Line 2 -->
                            <div class="md:col-span-2">
                                <label for="address_line_2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Address Line 2
                                </label>
                                <input
                                    v-model="form.address_line_2"
                                    type="text"
                                    id="address_line_2"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.address_line_2" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.address_line_2 }}</p>
                            </div>

                            <!-- City -->
                            <div>
                                <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    City
                                </label>
                                <input
                                    v-model="form.city"
                                    type="text"
                                    id="city"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.city" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.city }}</p>
                            </div>

                            <!-- State -->
                            <div>
                                <label for="state" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    State/Province
                                </label>
                                <input
                                    v-model="form.state"
                                    type="text"
                                    id="state"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.state" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.state }}</p>
                            </div>

                            <!-- Postal Code -->
                            <div>
                                <label for="postal_code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Postal Code
                                </label>
                                <input
                                    v-model="form.postal_code"
                                    type="text"
                                    id="postal_code"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.postal_code" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.postal_code }}</p>
                            </div>

                            <!-- Country -->
                            <div>
                                <label for="country" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                                    Country
                                </label>
                                <input
                                    v-model="form.country"
                                    type="text"
                                    id="country"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <p v-if="form.errors.country" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.country }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Status -->
                    <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <div class="flex items-center">
                            <input
                                v-model="form.is_active"
                                type="checkbox"
                                id="is_active"
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                            />
                            <label for="is_active" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                Active
                            </label>
                        </div>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Inactive locations won't appear in dropdown lists</p>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 rounded-b-lg flex justify-end space-x-3">
                    <Link
                        :href="route('settings.locations.show', location.id)"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="form.processing">Updating...</span>
                        <span v-else>Update Location</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';

const props = defineProps({
    location: Object,
    parentLocations: Array,
    locationTypes: Array,
});

const form = useSecureForm({
    name: props.location.name || '',
    code: props.location.code || '',
    parent_id: props.location.parent_id,
    type: props.location.type || '',
    address_line_1: props.location.address_line_1 || '',
    address_line_2: props.location.address_line_2 || '',
    city: props.location.city || '',
    state: props.location.state || '',
    postal_code: props.location.postal_code || '',
    country: props.location.country || '',
    description: props.location.description || '',
    is_active: props.location.is_active ?? true,
});

const submitForm = () => {
    form.put(route('settings.locations.update', props.location.id));
};
</script>
